import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Users, Image as ImgIcon, MapPin, Loader2 } from "lucide-react";
import { API } from "@/lib/auth";
import { BRIDAL_STATUS, STATUS_KEYS, StatusBadge, parseDate, daysUntil, countdownLabel } from "./bridalStatus";
import BridalEnquiryDetail from "./BridalEnquiryDetail";

const DateBlock = ({ iso }) => {
  const d = parseDate(iso);
  const n = daysUntil(iso);
  const urgent = n !== null && n >= 0 && n <= 30;
  return (
    <div className={`w-16 shrink-0 text-center rounded-2xl py-2 ${urgent ? "bg-[#8B1B3A] text-white" : "bg-[hsl(var(--blush)/0.5)] text-[hsl(var(--onyx))]"}`}>
      {d ? (
        <>
          <div className="text-[10px] uppercase tracking-wider opacity-80">{d.toLocaleDateString("en-GB", { month: "short" })}</div>
          <div className="font-display text-2xl leading-none">{d.getDate()}</div>
          <div className="text-[10px] opacity-70">{d.getFullYear()}</div>
        </>
      ) : <div className="text-[10px] px-1 py-2">{iso || "No date"}</div>}
    </div>
  );
};

const EnquiryCard = ({ e, onOpen }) => (
  <button onClick={onOpen} data-testid={`bridal-enquiry-card-${e.id}`}
    className="w-full text-left rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-4 sm:p-5 flex gap-4 hover:border-[hsl(var(--onyx)/0.25)] transition-colors duration-300">
    <DateBlock iso={e.wedding_date} />
    <div className="flex-1 min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-display text-xl truncate">{e.full_name}</span>
        <StatusBadge status={e.status} testId={`bridal-enquiry-status-${e.id}`} />
      </div>
      <div className="mt-1 text-xs text-[hsl(var(--onyx)/0.55)]">{countdownLabel(e.wedding_date)} · Ref {e.reference}</div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[hsl(var(--onyx)/0.7)]">
        <span className="inline-flex items-center gap-1 min-w-0"><MapPin size={12} /> <span className="truncate max-w-[200px]">{e.wedding_venue}</span></span>
        <span className="inline-flex items-center gap-1"><Users size={12} /> {e.people_count}</span>
        {e.inspiration_images?.length > 0 && <span className="inline-flex items-center gap-1"><ImgIcon size={12} /> {e.inspiration_images.length}</span>}
      </div>
    </div>
  </button>
);

export default function BridalEnquiries() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("active");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("wedding");
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/bridal/enquiries`);
      setData(r.data);
    } catch { toast.error("Could not load bridal enquiries"); }
  };
  useEffect(() => { load(); }, []);

  const replace = (doc) => setData((d) => ({ ...d, enquiries: d.enquiries.map((x) => (x.id === doc.id ? doc : x)) }));
  const remove = () => { setOpenId(null); load(); };

  const list = useMemo(() => {
    if (!data) return [];
    const term = q.trim().toLowerCase();
    let out = data.enquiries.filter((e) => {
      if (filter === "active" && ["wedding_complete", "declined"].includes(e.status)) return false;
      if (filter !== "active" && filter !== "all" && e.status !== filter) return false;
      if (!term) return true;
      return [e.full_name, e.email, e.phone, e.wedding_venue, e.reference].some((v) => (v || "").toLowerCase().includes(term));
    });
    if (sort === "wedding") out = [...out].sort((a, b) => (a.wedding_date || "9999").localeCompare(b.wedding_date || "9999"));
    return out;
  }, [data, filter, q, sort]);

  if (!data) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin" size={20} /></div>;

  const counts = data.enquiries.reduce((acc, e) => ({ ...acc, [e.status]: (acc[e.status] || 0) + 1 }), {});
  const activeCount = data.enquiries.filter((e) => !["wedding_complete", "declined"].includes(e.status)).length;
  const open = data.enquiries.find((e) => e.id === openId);
  const chip = (k, label, n) => (
    <button key={k} onClick={() => setFilter(k)} data-testid={`bridal-filter-${k}`}
      className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs transition-colors duration-300 ${filter === k ? "bg-[hsl(var(--onyx))] text-[hsl(var(--cream))]" : "bg-white border border-[hsl(var(--onyx)/0.1)] text-[hsl(var(--onyx)/0.7)]"}`}>
      {label} <span className="opacity-60">{n}</span>
    </button>
  );

  return (
    <div data-testid="bridal-enquiries-panel" className="space-y-5">
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {chip("active", "Active", activeCount)}
        {chip("all", "All", data.enquiries.length)}
        {STATUS_KEYS.map((k) => chip(k, BRIDAL_STATUS[k].l, counts[k] || 0))}
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[hsl(var(--onyx)/0.4)]" />
          <input data-testid="bridal-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, venue, reference…"
            className="w-full rounded-full border border-[hsl(var(--onyx)/0.12)] bg-white pl-10 pr-4 py-2.5 text-sm" />
        </div>
        <select data-testid="bridal-sort-select" value={sort} onChange={(e) => setSort(e.target.value)}
          className="rounded-full border border-[hsl(var(--onyx)/0.12)] bg-white px-4 py-2.5 text-sm">
          <option value="wedding">Wedding date (soonest first)</option>
          <option value="newest">Newest enquiry first</option>
        </select>
      </div>
      {list.length === 0 ? (
        <div data-testid="bridal-enquiries-empty" className="rounded-3xl bg-white border border-dashed border-[hsl(var(--onyx)/0.15)] p-10 text-center text-sm text-[hsl(var(--onyx)/0.55)]">
          No bridal enquiries here yet.
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-3">
          {list.map((e) => <EnquiryCard key={e.id} e={e} onOpen={() => setOpenId(e.id)} />)}
        </div>
      )}
      <BridalEnquiryDetail enquiry={open} onClose={() => setOpenId(null)} onChange={replace} onDeleted={remove} />
    </div>
  );
}
